/**
 * Literais das permissions de usuário (`app/permissions/permissions.py::UsersPermissions`).
 *
 * Existe como const pelo mesmo motivo de [[contacts]]: o contract test de
 * `literals.test.ts` só alcança CONSTS, e `check.ts` faz `includes()` exato — não
 * há wildcard `users:*`, então literal errado é fail-closed e MUDO (a afordância
 * some da tela sem erro nenhum).
 *
 * `HARD_DELETE` e `HISTORY_READ` são **runtime-only**: fora de todo preset do back,
 * concedidas na mão por tenant. Nem `atendente` nem `owner` as têm por default.
 */
export const USERS_PERMISSIONS = {
  READ: 'users:read',
  CREATE: 'users:create',
  UPDATE: 'users:update',
  /** Soft delete (`DELETE /users/{id}`) — desativa. NÃO habilita o hard. */
  DELETE: 'users:delete',
  /** Hard delete (`DELETE /users/{id}?permanent=true`) — doc do tenant + user
   *  global. Runtime-only. */
  HARD_DELETE: 'users:hard_delete',
  CHANNELS_MANAGE: 'users:channels:manage',
  PASSWORD_RESET: 'users:password:reset',
  /**
   * `GET /users/{id}/events`. **`READ` NÃO dá o histórico** — são grants
   * independentes, e o back trava isso
   * (`tests/unit/routes/test_user_events_route.py::test_sem_a_permission_e_403`).
   * Runtime-only, mesmo regime de `contacts:history:read`: a aba **nasce sem
   * afordância** até o grant existir por tenant, e isso é ESTADO esperado.
   */
  HISTORY_READ: 'users:history:read',
  /**
   * `GET /users/directory` — roster de IDENTIDADE (db_id, nome, @username, role,
   * active, avatar). É o que resolve nome de autor em toda superfície do app.
   * **NÃO é `READ`**: `users:read` é o grant de GESTÃO (`GET /users` com e-mail,
   * permissions, escopos e canais). Quem só precisa saber quem escreveu não
   * precisa do dossiê inteiro do time.
   */
  DIRECTORY_READ: 'users:directory:read',
  /**
   * `GET /users/presence` **e** a sala do socket: sem este grant o back não põe
   * o cliente na sala, então `presence_changed` nem chega. Eixo separado do
   * directory de propósito — presença é onde a pessoa está agora, identidade é
   * quem ela é. Um tenant pode querer nomes sem vigilância de estado.
   */
  PRESENCE_READ: 'users:presence:read',
  /**
   * Cargo dos colegas: com ela o `GET /users/directory` traz `sub_roles:
   * [{key, name}]`; sem ela o campo vem `null`. Não gateia rota nem tela — é
   * o back que decide o campo; a const existe pro contract test.
   */
  DIRECTORY_SUB_ROLES_READ: 'users:directory:sub_roles:read',
  /**
   * `GET /users/navigation` **e** as salas `{t}:nav:{canal}` do socket
   * (`enter_navigation`): quem está com cada canal aberto agora. Fora de todo
   * preset do back; o dstorres herda. Sozinha não mostra nada — a tela junta
   * navegação × presença × directory, e o gate de UI exige as três
   * (`lib/navigation/navegacao.ts`, `podeVerNavegacao`).
   */
  NAVIGATION_READ: 'users:navigation:read',
  /**
   * Sessões de login, dispositivos (`jkt`) e rotas lentas de outro user (`GET /users/{id}/sessions|devices|
   * slow-routes`; `/networks` exige também `audit:network:read`) — o alvo estritamente abaixo, ou qualquer um (a si
   * e outro `dstorres` inclusive) quando quem olha é `dstorres`. Runtime-only, fora de todo preset. A própria pessoa
   * lê sessões/dispositivos/redes por `/auth/*`, sem permission (a própria ficha de `/admin/users/[id]` usa essas);
   * rotas lentas não têm versão self. Gateia a leitura admin do grupo "Dispositivos e redes" da aba Acessos.
   */
  SESSIONS_READ: 'users:sessions:read',
  /**
   * Desconectar sessão (`DELETE /users/{id}/sessions/{sid}`) ou dispositivo (`DELETE /users/{id}/devices/{jkt}`) de
   * OUTRO user, alvo estritamente abaixo. Runtime-only, fora de todo preset (back `permissions.py:127-131`). Grant
   * INDEPENDENTE de `SESSIONS_READ`: ler não dá o botão.
   */
  SESSIONS_REVOKE: 'users:sessions:revoke',
} as const
